import React, { useState } from 'react';
import { Box, Typography, TextField, Divider, IconButton, MenuItem, Select, FormControl } from '@mui/material';
import EditIcon from '../../../assets/icons/EditBlue.svg';
import PinkGradientButton from '../../UIControls/PinkGradientButton';

const SettingsUser = ({
    clinicianData,
    handleChangeInput,
    newEmail,
    setNewEmail,
    updateEmail,
    updateClinicData,
    updateClinicDataMultiuser,
    isMultiUser = false,
    canEdit = true,
    showEditAction = true,
    sectionSx = {},
    layout = 'stacked',
}) => {
    const [editorActive, setEditorActive] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const fieldDescriptionSx = {
        color: '#616063',
        fontFamily: 'Inter',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '14px',
        letterSpacing: '-0.28px',
        mb: '10px',
    };

    const handleEmailChange = (e) => {
        setNewEmail(e.target.value);
        if (validationErrors['userData.emailAddress']) {
            setValidationErrors((prev) => ({ ...prev, 'userData.emailAddress': '' }));
        }
    };

    const validatePersonalDetails = () => {
        const errors = {};
        const firstName = (clinicianData?.userData?.firstName || '').trim();
        const lastName = (clinicianData?.userData?.lastName || '').trim();
        const emailValue = isMultiUser
            ? (clinicianData?.userData?.emailAddress || '').trim()
            : (newEmail || clinicianData?.userData?.emailAddress || '').trim();

        if (!firstName) errors['userData.firstName'] = 'First name is required';
        if (!lastName) errors['userData.lastName'] = 'Last name is required';
        if (!emailValue) {
            errors['userData.emailAddress'] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
            errors['userData.emailAddress'] = 'Enter a valid email address';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveChanges = async () => {
        if (!canEdit) return;
        if (!validatePersonalDetails()) return;
        if (!isMultiUser && newEmail !== clinicianData.userData.emailAddress) {
            await updateEmail(newEmail);
        }

        if (isMultiUser) {
            await updateClinicDataMultiuser();
        } else {
            await updateClinicData();
        }
    };

    const renderTextField = (label, placeholder, name, value, onChange, isLast = false, readOnly = false) => (
        <Box key={name} sx={{ marginBottom: isLast ? 0 : 2, position: 'relative', minWidth: 0 }}>
            {layout === 'two-column' && (
                <Typography sx={fieldDescriptionSx}>
                    {label}
                </Typography>
            )}
            <TextField
                name={name}
                variant="outlined"
                placeholder={placeholder}
                value={value}
                onChange={
                    readOnly || !editorActive || !canEdit
                        ? null
                        : (e) => {
                              onChange(e);
                              if (validationErrors[name]) {
                                  setValidationErrors((prev) => ({ ...prev, [name]: '' }));
                              }
                          }
                }
                size="small"
                fullWidth
                error={!!validationErrors[name]}
                helperText={validationErrors[name] || ''}
                InputProps={{
                    readOnly: readOnly || !editorActive || !canEdit,
                    style: {
                        border: 'none',
                        background: editorActive && !readOnly ? '#F7F7F7' : 'transparent',
                        marginBottom: layout === 'two-column' ? 0 : isLast ? 0 : 16,
                    },
                    disableUnderline: true,
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: layout === 'two-column' ? '42.923px' : undefined,
                        borderRadius: layout === 'two-column' ? '9.198px' : undefined,
                        '& fieldset': {
                            border: layout === 'two-column' ? '0.766px solid rgba(102, 102, 102, 0.35)' : 'none',
                        },
                    },
                    input: {
                        height: layout === 'two-column' ? '42.923px' : 56,
                        color: 'rgb(33, 33, 33)',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 'none',
                        order: 0,
                        alignSelf: 'stretch',
                        flexGrow: 0,
                        p: 0,
                        pl: layout === 'two-column' ? '9px' : editorActive && !readOnly ? 2 : 0,
                    },
                }}
            />
            {layout !== 'two-column' && !isLast && (
                <Divider
                    sx={{
                        width: 'calc(100% + 48px)',
                        position: 'absolute',
                        bottom: 0,
                        left: '-24px',
                        backgroundColor: 'rgba(229, 229, 229, 1)',
                        display: editorActive ? 'none' : 'block',
                    }}
                />
            )}
        </Box>
    );

    const renderExperienceField = () => (
        <Box key="yearsExperience" sx={{ marginBottom: '16px', position: 'relative', borderColor: '#F7F7F7', minWidth: 0 }}>
            {layout === 'two-column' && (
                <Typography sx={fieldDescriptionSx}>
                    Years of experience
                </Typography>
            )}
            {editorActive && canEdit ? (
                <FormControl fullWidth sx={{ minWidth: 0 }}>
                    <Select
                        name="userData.yearsExperience"
                        value={clinicianData.userData.yearsExperience}
                        onChange={handleChangeInput}
                        fullWidth
                        variant="outlined"
                        displayEmpty
                        inputProps={{ 'aria-label': 'Please select your years of experience' }}
                        renderValue={(selected) => {
                            if (!selected) {
                                return <div>Please select your years of experience</div>;
                            }
                            return selected;
                        }}
                        sx={{
                            mb: 2,
                            borderRadius: '9.198px',
                            background: '#F7F7F7',
                            height: layout === 'two-column' ? '42.923px' : 56,
                            pl: editorActive ? 2 : 0,
                            '& .MuiOutlinedInput-input': {
                                padding: 0,
                                paddingLeft: layout === 'two-column' ? '9px' : 0,
                                height: layout === 'two-column' ? '42.923px' : undefined,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: layout === 'two-column' ? '0.766px solid rgba(102, 102, 102, 0.35)' : undefined,
                            },
                            '& .MuiSelect-select': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                        }}
                    >
                        <MenuItem value="" disabled>
                            Please select your years of experience
                        </MenuItem>
                        <MenuItem value="0-3 years">0-3 years</MenuItem>
                        <MenuItem value="4-10 years">4-10 years</MenuItem>
                        <MenuItem value="10+ years">10+ years</MenuItem>
                    </Select>
                </FormControl>
            ) : (
                <TextField
                    name="userData.yearsExperience"
                    variant="outlined"
                    value={clinicianData.userData.yearsExperience}
                    placeholder="Years of experience"
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        style: {
                            border: 'none',
                            background: 'transparent',
                            marginBottom: layout === 'two-column' ? 0 : 16,
                        },
                        disableUnderline: true,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: layout === 'two-column' ? '42.923px' : undefined,
                            borderRadius: layout === 'two-column' ? '9.198px' : undefined,
                            '& fieldset': {
                                border: layout === 'two-column' ? '0.766px solid rgba(102, 102, 102, 0.35)' : 'none',
                            },
                        },
                        input: {
                            height: layout === 'two-column' ? '42.923px' : 56,
                            color: 'rgb(33, 33, 33)',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 'none',
                            order: 0,
                            alignSelf: 'stretch',
                            flexGrow: 0,
                            p: 0,
                            pl: layout === 'two-column' ? '9px' : editorActive ? 2 : 0,
                        },
                    }}
                />
            )}
            {layout !== 'two-column' && (
            <Divider
                sx={{
                    width: 'calc(100% + 48px)',
                    position: 'absolute',
                    bottom: 0,
                    left: '-24px',
                    backgroundColor: 'rgba(229, 229, 229, 1)',
                    display: editorActive ? 'none' : 'block',
                }}
            />
            )}
        </Box>
    );

    return (
        <Box
            className="settings-block"
            sx={{
                backgroundColor: 'white',
                borderRadius: '30px',
                border: '1px solid rgba(102, 102, 102, 0.15)',
                padding: { xs: '20px 22px', md: '24px 30px' },
                my: 3,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                ...sectionSx,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, height: 48, flex: 1, minWidth: 0 }}>
                    Personal details
                </Typography>
                {showEditAction && (
                    <IconButton
                        onClick={() => setEditorActive(!editorActive)}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        sx={{
                            p: 0,
                            width: 38,
                            height: 38,
                            borderRadius: '38px',
                            bgcolor: editorActive ? '#000453' : '#FF669B',
                            '&:hover': { bgcolor: editorActive ? '#1C2A8A' : '#FF85B0' },
                            '&:active': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                            '&:focus': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                            '&.Mui-focusVisible': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                            WebkitTapHighlightColor: 'transparent',
                            flexShrink: 0,
                        }}
                    >
                        <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
                    </IconButton>
                )}
            </Box>

            {layout === 'two-column' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2, rowGap: 1, minWidth: 0 }}>
                    {renderTextField('First name', 'First name', 'userData.firstName', clinicianData.userData.firstName, handleChangeInput)}
                    {renderTextField('Last name', 'Last name', 'userData.lastName', clinicianData.userData.lastName, handleChangeInput)}
                    {renderTextField(
                        'Email',
                        'Email address',
                        'userData.emailAddress',
                        newEmail ? newEmail : clinicianData.userData.emailAddress,
                        handleEmailChange,
                        false,
                        isMultiUser,
                    )}
                    {renderTextField('Mobile number', 'Mobile number', 'userData.mobile', clinicianData.userData.mobile, handleChangeInput)}
                    {renderTextField('Occupation', 'Job title', 'userData.jobTitle', clinicianData.userData.jobTitle, handleChangeInput)}
                    {renderExperienceField()}
                </Box>
            ) : (
                <>
                    {renderTextField('First name', 'First name', 'userData.firstName', clinicianData.userData.firstName, handleChangeInput)}
                    {renderTextField('Last name', 'Last name', 'userData.lastName', clinicianData.userData.lastName, handleChangeInput)}
                    {renderTextField('Job title', 'Job title', 'userData.jobTitle', clinicianData.userData.jobTitle, handleChangeInput)}
                    {renderTextField(
                        'Email address',
                        'Email address',
                        'userData.emailAddress',
                        newEmail ? newEmail : clinicianData.userData.emailAddress,
                        handleEmailChange,
                        false,
                        isMultiUser,
                    )}
                    {renderExperienceField()}
                    {renderTextField(
                        'Mobile number',
                        'Mobile number',
                        'userData.mobile',
                        clinicianData.userData.mobile,
                        handleChangeInput,
                        true,
                    )}
                </>
            )}

            {editorActive && canEdit && (
                <Box display="flex" flexDirection="column" gap={2}>
                    <PinkGradientButton
                        className="black-btn"
                        onClick={handleSaveChanges}
                        sx={{
                            marginTop: 3,
                            mx: { xs: 'auto', md: 0 },
                            display: 'block',
                            height: '44px',
                            borderRadius: '200px',
                            background: '#FF669B',
                            backgroundImage: 'none',
                            '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                            color: '#FFF',
                        }}
                    >
                        Save changes
                    </PinkGradientButton>
                </Box>
            )}
        </Box>
    );
};

export default SettingsUser;
